import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { hasTargets } from "@/lib/verticals";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Receipt, Target,
  Settings as SettingsIcon, Menu, LogOut, ChevronDown, Sun, Moon,
  PiggyBank, FileText, History, ShieldCheck, BarChart3, CalendarDays,
  PanelLeftClose, PanelLeft, Store, Gift, FileEdit, ScanSearch,
} from "lucide-react";
import sMonoSand from "@/assets/s_mono_sand.png";

// roles: "full" (admin/leader) | "manager" | "executive"; omit => visible to all
const NAV = [
  { group: "Overview", items: [
    { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["full", "manager"] },
    { label: "Marketing Plan", to: "/marketing-plan", icon: CalendarDays, testid: "nav-marketing-plan", roles: ["full", "manager"] },
    { label: "Analytics", to: "/analytics", icon: BarChart3, testid: "nav-analytics", roles: ["full"] },
  ] },
  { group: "Planning", items: [
    { label: "Targets", to: "/targets", icon: Target, testid: "nav-targets", roles: ["full", "manager"] },
    { label: "Budget Allocation", to: "/budget-allocation", icon: PiggyBank, testid: "nav-budget-allocation", roles: ["full"] },
  ] },
  { group: "Finance", items: [
    { label: "Spends Log", to: "/spends", icon: Receipt, testid: "nav-spends-log" },
    { label: "Invoices", to: "/invoices", icon: FileText, testid: "nav-invoices" },
    { label: "Vendor Scorecards", to: "/vendors", icon: Store, testid: "nav-vendors", roles: ["full", "executive"] },
    { label: "Referral Tracker", to: "/referrals", icon: Gift, testid: "nav-referrals", roles: ["full"] },
  ] },
  { group: "Governance", items: [
    { label: "Audit Log", to: "/governance/audit-log", icon: History, testid: "nav-audit-log", roles: ["full"] },
    { label: "Data Integrity", to: "/governance/data-integrity", icon: ScanSearch, testid: "nav-data-integrity", roles: ["full"], adminOnly: true },
  ] },
];
const ADMIN_NAV = { group: "Administration", items: [
  { label: "Content & CMS", to: "/admin/cms", icon: FileEdit, testid: "nav-admin-cms", roles: ["full"], adminOnly: true },
  { label: "Settings", to: "/settings", icon: SettingsIcon, testid: "nav-settings", roles: ["full"] },
] };

const PAGE_TITLES = {
  "/dashboard": "Dashboard", "/analytics": "Analytics",
  "/marketing-plan": "Marketing Plan", "/marketing-strategy": "Marketing Strategy",
  "/budget-allocation": "Budget Allocation",
  "/spends": "Spends Log",
  "/vendors": "Vendor Scorecards",
  "/referrals": "Referral Tracker",
  "/targets": "Targets", "/invoices": "Invoice & Payments",
  "/governance/audit-log": "Audit Log", "/settings": "Settings",
  "/governance/data-integrity": "Data Integrity",
  "/admin/cms": "Content & CMS",
};

const ROLE_STYLES = {
  admin: "bg-[hsl(var(--primary)/0.12)] text-primary",
  leader: "bg-[hsl(var(--primary)/0.12)] text-primary",
  manager: "bg-[hsl(var(--info)/0.14)] text-[hsl(var(--info))]",
  executive: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))]",
  member: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))]",
};
const ROLE_LABEL = { admin: "Admin", leader: "Leader", manager: "Manager", executive: "Executive", member: "Executive" };

function effectiveRole({ isFull, isManager }) {
  if (isFull) return "full";
  if (isManager) return "manager";
  return "executive";
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-4 h-16 border-b">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
        <img src={sMonoSand} alt="Sumadhura monogram" className="h-6 w-auto" data-testid="sidebar-brand-logo" />
      </div>
      <div className="leading-tight">
        <p className="text-sm font-semibold font-heading tracking-[0.08em]">SUMADHURA</p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Marketing Command</p>
      </div>
    </div>
  );
}

function NavItems({ onNavigate }) {
  const auth = useAuth();
  const er = effectiveRole(auth);
  const groups = auth.isFull ? [...NAV, ADMIN_NAV] : NAV;
  // Vertical leads without admin-granted dashboard access don't see the Dashboard link.
  const hideDashboard = auth.isManager && !auth.dashboardAccess;
  // Vertical leads whose assigned verticals have no applicable targets don't see Targets.
  const hideTargets = auth.isManager && !hasTargets(auth.assignedVerticals);
  const visible = groups
    .map((grp) => ({ ...grp, items: grp.items.filter((it) => (!it.roles || it.roles.includes(er)) && (!it.adminOnly || auth.isCmsAdmin) && !(it.to === "/dashboard" && hideDashboard) && !(it.to === "/targets" && hideTargets)) }))
    .filter((grp) => grp.items.length > 0);
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
      {visible.map((grp) => (
        <div key={grp.group}>
          <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{grp.group}</p>
          <div className="space-y-0.5">
            {grp.items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={it.testid} onClick={onNavigate}
                className={({ isActive }) => cn(
                  "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors duration-150",
                  isActive ? "bg-[hsl(var(--primary)/0.10)] text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}>
                {({ isActive }) => (
                  <>
                    <span className={cn("h-5 w-0.5 rounded-full", isActive ? "bg-primary" : "bg-transparent")} />
                    <it.icon className="h-4 w-4" />{it.label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme"
      className="h-9 w-9">
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

function CurrencyToggle() {
  const { mode, setMode } = useCurrency();
  return (
    <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v)} data-testid="currency-format-toggle" className="rounded-lg border bg-card p-0.5">
      <ToggleGroupItem value="crore" data-testid="currency-view-crore" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">₹ Cr</ToggleGroupItem>
      <ToggleGroupItem value="full" data-testid="currency-view-full" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Full ₹</ToggleGroupItem>
    </ToggleGroup>
  );
}

function UserMenu() {
  const { user, logout, isManager, assignedVerticals } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const initials = (user.name || user.email).split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const doLogout = () => { logout(); navigate("/login", { replace: true }); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-lg border bg-card px-2 py-1.5 hover:bg-secondary" data-testid="user-menu-trigger">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">{initials}</div>
          <span className="hidden text-xs font-medium text-foreground sm:block">{user.name}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-muted-foreground">{user.email}</p>
          <span className={cn("mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
            ROLE_STYLES[user.role] || ROLE_STYLES.executive)} data-testid="user-role-badge">
            {ROLE_LABEL[user.role] || user.role}
          </span>
          {isManager && assignedVerticals.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid="user-assigned-verticals">
              {assignedVerticals.map((v) => (
                <span key={v} className="rounded-md bg-secondary px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">{v}</span>
              ))}
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={doLogout} className="text-[hsl(var(--danger))]" data-testid="logout-button">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell({ children }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("mt_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("mt_sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });
  const title = PAGE_TITLES[location.pathname]
    || (location.pathname.startsWith("/projects/") ? "Project Detail" : "")
    || "Dashboard";

  return (
    <div className="app-noise min-h-screen bg-background">
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 hidden w-[260px] flex-col border-r bg-card transition-transform duration-200 lg:flex",
        collapsed && "lg:-translate-x-full"
      )} data-testid="app-sidebar">
        <Brand />
        <NavItems />
        <div className="border-t px-4 py-3">
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> FY 2026-27 · Role-based access
          </p>
        </div>
      </aside>

      <header className={cn(
        "sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b bg-card/90 px-4 backdrop-blur lg:pr-6",
        collapsed ? "lg:pl-4" : "lg:pl-[276px]"
      )}>
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-nav-trigger" aria-label="Open navigation"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0 overflow-y-auto"><Brand /><NavItems onNavigate={() => setMobileOpen(false)} /></SheetContent>
          </Sheet>
          <Button variant="ghost" size="icon" onClick={toggleCollapsed} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden lg:inline-flex" data-testid="sidebar-collapse-toggle">
            {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </Button>
          <h2 className="text-base font-semibold font-heading" data-testid="page-title">{title}</h2>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <CurrencyToggle />
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <main className={cn(
        "relative z-[1] px-4 py-5 sm:px-6 lg:pr-8",
        collapsed ? "lg:pl-6" : "lg:pl-[284px]"
      )}>
        <div className={cn("mx-auto", collapsed ? "max-w-[1760px]" : "max-w-[1480px]")}>{children}</div>
      </main>
    </div>
  );
}
